/* eslint-disable complexity */
import {
	getLabelValue,
	IPlatesValidatedState,
	ITypeLicensePlateFields,
	OfferContext,
	VignetteContext,
} from '@adac-mp/shared';
import {
	ArrowRight,
	Button,
	Checkbox,
	Drawer,
	DrawerContent,
	HintMessage,
	PlatePubliclyVisibleCheckbox,
	getDrawerTitle,
	getHintContent,
} from '@adac-mp/components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useValidatedStates } from '../../hooks/useValidatedStates';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useViewport } from '../../hooks/useViewport';
import { Typography, IPlatesInputProps, PlatesInput } from '../../index';
import { ContentfulDrawerContent } from '../ContentfulDrawerContent';
import { trackInfoClick } from '../../utils/tracking/tracking';

export const PlatesInputConfirmation = ({
	border = false,
	hasSpecialPlates = false,
	withAutoFocus = false,
	insideOfModal = false,
	countries,
	hasError = false,
	plates,
	country,
	onChange,
	setCountry,
	setHideModal,
	'data-cy': dataCy = 'plates-part',
}: IPlatesInputProps) => {
	const { contentfulLabels, platePubliclyVisible, selectedCountries, setPlatePubliclyVisible } =
		useContext(VignetteContext);
	const { labels } = useContext(OfferContext);
	const licensePlatesLabels = contentfulLabels?.cart?.licensePlates as ITypeLicensePlateFields;
	const [state, { updatePlatesWithConfirmationAndError }] = useValidatedStates({
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		confirmedPlates: { value: [''], error: false, specialPlates: plates.specialPlates } as IPlatesValidatedState,
	});
	const [platesMatch, setPlatesMatch] = useState(false);
	const platesRef = useRef<HTMLInputElement[]>([]);
	const confirmationPlatesRef = useRef<HTMLInputElement[]>([]);
	const [editingPlates, setEditingPlates] = useState(true);
	const previousCountry = usePreviousValue(country);
	const [specialPlatesSelected, setSpecialPlatesSelected] = useState(plates.specialPlates);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const { viewport } = useViewport();
	const isSchweizSelected = selectedCountries.find((selectedCountry) => selectedCountry.supplierId === 14);

	const platesCorrectlyEntered = Array.isArray(plates.value) && plates.value.join('').length > 0 && !plates.error;

	const platesConfirmed =
		state.confirmedPlates.value.join('').length > 0 && !state.confirmedPlates.error && !platesMatch;

	const updateAllPlates = (
		updatedPlates: IPlatesValidatedState,
		updatedConfirmedPlates: IPlatesValidatedState | undefined = undefined,
	) => {
		const { value, error, confirmed = false, countryCode, specialPlates } = updatedConfirmedPlates ?? updatedPlates;
		if (value) {
			updatePlatesWithConfirmationAndError(
				'confirmedPlates',
				value,
				error,
				confirmed,
				countryCode,
				specialPlates,
			);
		}
		onChange(updatedPlates);
	};

	const focusOnFirstConfirmationInput = () => {
		if (platesCorrectlyEntered) {
			confirmationPlatesRef.current.find((c) => c.id.includes(`${dataCy}-0`))?.focus();
		}
	};

	useEffect(() => {
		if (editingPlates && country) {
			const updatedPlates = { ...plates, confirmed: false, specialPlates: specialPlatesSelected };
			const updatedConfirmedPlates = {
				value: [''],
				error: false,
				confirmed: false,
				countryCode: country.isoCode,
				specialPlates: specialPlatesSelected,
			};
			updateAllPlates(updatedPlates, updatedConfirmedPlates);
		}
	}, [editingPlates]);

	useEffect(() => {
		if (country && previousCountry && previousCountry !== country) {
			setEditingPlates(true);
			const updatedPlates = {
				...plates,
				value: [''],
				error: false,
				confirmed: false,
				countryCode: country.isoCode,
				specialPlates: specialPlatesSelected,
			};
			updateAllPlates(updatedPlates);
			platesRef.current[0].focus();
		}
	}, [country]);

	const getSpecialPlatesCheckBoxText = () => {
		if (country?.isoCode === 'DE') {
			return (
				licensePlatesLabels?.checkboxLabelDe ||
				'(Kurzzeit-, Export-, Übergangs-, Diplomaten-, Behörden- oder Rotes Kennzeichen)'
			);
		}
		if (country?.isoCode === 'AT') {
			return (
				licensePlatesLabels?.checkboxLabelAt ||
				'(Behörden-, Regierungs-, Diplomaten-, Militär,- oder schwarzes Kennzeichen)'
			);
		}

		return licensePlatesLabels?.checkboxLabelRest || '(z.B. Saison-, Diplomaten-, oder Behördenkennzeichen)';
	};

	const renderSpecialPlatesCheckbox = () => (
		<Checkbox
			className="my-4 pl-3"
			data-cy="button: specialPlates"
			label={
				<>
					<Typography component="p" color="title" variant="title">
						{licensePlatesLabels?.checkboxLabelLicenseplate || 'Ich habe ein Sonderkennzeichen.'}
					</Typography>
					<Typography color="text">{getSpecialPlatesCheckBoxText()}</Typography>
				</>
			}
			id={`Special Plates--${insideOfModal ? '1' : '0'}`}
			title="Special Plates"
			checked={specialPlatesSelected}
			setChecked={() => {
				setSpecialPlatesSelected(!specialPlatesSelected);
				setEditingPlates(true);
				const updatedPlates = {
					...plates,
					value: [''],
					error: false,
					confirmed: false,
					countryCode: state.isoCode,
					specialPlates: !specialPlatesSelected,
				};
				updateAllPlates(updatedPlates);
				platesRef.current[0].focus();
			}}
		/>
	);

	const getSpecialPlatesHintID = () => {
		if (country?.isoCode === 'DE') {
			return 'hintSpecialLicensePlateDE';
		}
		if (country?.isoCode === 'AT') {
			return 'hintSpecialLicensePlateAUT';
		}
		return 'hintSpecialLicensePlateDE';
	};

	const onHintClick = () => {
		setDrawerOpen(true);
		trackInfoClick({
			label: 'Hilfe zur Eingabe von Sonderkennzeichen',
			element: 'Kennzeichen',
			category: 'Content',
		});
		if (setHideModal) {
			setHideModal(true);
		}
	};

	const renderSpecialPlateHintMessage = () => (
		<HintMessage
			className={`transition-all duration-300 ${
				specialPlatesSelected ? 'animate-fadeSlideIn' : 'animate-fadeSlideOut'
			}`}
			padding={`${insideOfModal ? 'small' : 'large'}`}
			hints={getHintContent(
				getSpecialPlatesHintID(),
				[
					{
						message: 'Bitte geben Sie einen Bindestrich anstelle der Plakette ein.',
						link: { text: 'Hilfe zur Eingabe von Sonderkennzeichen', onClick: onHintClick },
					},
				],
				licensePlatesLabels?.hintMessages,
				onHintClick,
			)}
		/>
	);

	const renderPlatesHintMessage = () => (
		<Button
			size="sm"
			variant="tertiary"
			className="gap-2 mt-3 self-center bg-transparent group"
			onClick={() => {
				setDrawerOpen(true);
				if (setHideModal) {
					setHideModal(true);
				}
			}}>
			<ArrowRight className="group-hover:[&>*]:stroke-gray-100" />
			{licensePlatesLabels?.checkboxLabelLicenseplate || 'Ich habe ein Sonderkennzeichen.'}
		</Button>
	);

	const renderNormalPlatesAlternativeHintMessage = () => (
		<HintMessage
			className={`transition-all duration-300 ${
				!specialPlatesSelected ? 'animate-fadeSlideIn' : 'animate-fadeSlideOut'
			}`}
			padding={`${insideOfModal ? 'small' : 'large'}`}
			hints={getHintContent(
				'hintLicenseplateDE',
				[
					{
						message: (
							<>
								Bei den Endungen E (elektrifiziertes Fahrzeug) bzw. H (historisches Fahrzeug) fügen Sie
								den jeweiligen Buchstaben in das Zahlenfeld der Eingabemaske ohne Leerzeichen ein, z.B.
								D-AB1234E bzw. D-AB1234H.
								<br />
								Bei Saison- oder Wechselkennzeichen geben Sie bitte nur das reine Kennzeichen ein, also
								ohne Gültigkeitszeitraum am Ende.
							</>
						),
					},
				],
				licensePlatesLabels?.hintMessages,
			)}
		/>
	);

	const getDrawerId = () => {
		if (country?.isoCode === 'DE') {
			return 'specialLicensePlateDe';
		}
		if (country?.isoCode === 'AT') {
			return 'specialLicensePlateAut';
		}
		return 'Sonderkennzeichen';
	};

	const renderPlatePubliclyVisibleCheckbox = () => (
		<PlatePubliclyVisibleCheckbox
			platePubliclyVisible={platePubliclyVisible}
			label={
				getLabelValue('mp.platePubliclyVisible', labels) ||
				'Kennzeichen in der Schweiz öffentlich einsehbar machen.'
			}
			setPlatePubliclyVisible={setPlatePubliclyVisible}
		/>
	);

	const renderSpecialPlatesContent = () => (
		<>
			{renderSpecialPlatesCheckbox()}
			{isSchweizSelected && renderPlatePubliclyVisibleCheckbox()}
			{!specialPlatesSelected && country?.isoCode === 'DE' && renderNormalPlatesAlternativeHintMessage()}
			{specialPlatesSelected &&
				(country?.isoCode === 'DE' || country?.isoCode === 'AT') &&
				renderSpecialPlateHintMessage()}
		</>
	);

	const getDrawerFallbackTitle = () => {
		if (country?.isoCode === 'DE') {
			return 'Sonderkennzeichen für Deutschland';
		}
		if (country?.isoCode === 'AT') {
			return 'Sonderkennzeichen für Österreich';
		}
		return '';
	};

	return (
		<div className={`relative z-10 flex flex-col ${viewport === 'desktop' ? 'overflow-hidden' : ''}`}>
			<div
				onBlur={(e) => {
					if (!e.currentTarget.contains(e.relatedTarget)) {
						setTimeout(() => focusOnFirstConfirmationInput(), 200);
					}
				}}>
				<PlatesInput
					ref={platesRef}
					withAutoFocus={withAutoFocus}
					insideOfModal={insideOfModal}
					data-cy={dataCy}
					border={border}
					countries={countries}
					country={country}
					plates={plates}
					type={platesCorrectlyEntered && !plates.confirmed && !editingPlates ? 'password' : 'text'}
					licensePlatesLabels={licensePlatesLabels}
					setCountry={setCountry}
					onChange={(value) => onChange(value)}
					onFocus={(ev) => {
						if (!(ev.target instanceof HTMLButtonElement)) {
							setEditingPlates(true);
						}
					}}
					onBlur={() => setEditingPlates(false)}
				/>
			</div>
			<div id="confirmed-plates">
				<Typography className="pt-4 pb-3 pl-4" component="p" color="title">
					{licensePlatesLabels?.repeatLicenseplate || 'Wiederholung des Kennzeichens'}
				</Typography>
				<PlatesInput
					ref={confirmationPlatesRef}
					data-cy="confirmation-plates-part"
					border={border}
					insideOfModal={insideOfModal}
					countries={countries}
					country={country}
					plates={state.confirmedPlates}
					setCountry={setCountry}
					disabled={!platesCorrectlyEntered}
					confirmedError={platesCorrectlyEntered && (platesConfirmed || hasError)}
					licensePlatesLabels={licensePlatesLabels}
					onChange={({ value, error }) => {
						if (Array.isArray(value) && Array.isArray(plates.value) && country) {
							const platesAreEqual = value.join('-') === plates.value.join('-');
							const updatedPlates = {
								...plates,
								confirmed: platesAreEqual,
								countryCode: country.isoCode,
								specialPlates: specialPlatesSelected,
							};
							const updatedConfirmedPlates = {
								value,
								error,
								confirmed: platesAreEqual,
								countryCode: country.isoCode,
								specialPlates: specialPlatesSelected,
							};
							setPlatesMatch(platesAreEqual);
							updateAllPlates(updatedPlates, updatedConfirmedPlates);
						}
					}}
				/>
			</div>
			{country?.isoCode === 'DE' && !hasSpecialPlates && renderPlatesHintMessage()}
			{hasSpecialPlates && renderSpecialPlatesContent()}
			<Drawer
				isOpen={drawerOpen}
				insideOfModal={insideOfModal}
				paddingX="large"
				setIsOpen={(isOpen) => {
					setDrawerOpen(isOpen);
					if (setHideModal) {
						setHideModal(isOpen);
					}
				}}
				title={getDrawerTitle(getDrawerId(), licensePlatesLabels?.helpDrawers) || getDrawerFallbackTitle()}>
				{hasSpecialPlates ? (
					<ContentfulDrawerContent id={getDrawerId()} />
				) : (
					<DrawerContent
						title="Umgang mit Sonderkennzeichen"
						content={
							<>
								<Typography color="text" size="xlSpace">
									Bei den Endungen E (elektrifiziertes Fahrzeug) bzw. H (historisches Fahrzeug) fügen
									Sie den jeweiligen Buchstaben in das Zahlenfeld der Eingabemaske ohne Leerzeichen
									ein, z.B. D-AB1234E bzw. D-AB1234H.
								</Typography>
								<Typography color="text" size="xlSpace">
									Bei Saison- oder Wechselkennzeichen geben Sie bitte nur das reine Kennzeichen ein,
									z.B. D-AB1234 - also ohne Gültigkeitsmonate bzw. Zahlentafel am Ende.
								</Typography>
							</>
						}
					/>
				)}
			</Drawer>
		</div>
	);
};
